import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Product } from '@kimuno/api-sdk/lib/types/responses/Product';
import { Subject, dashboardSection, routes } from '../../../constants';
import { SearchFilters, SearchResultsTab } from '../../../types/search';
import useRouter from '../../../utils/useRouter';
import DashboardMuiCard from '../../cards/MuiCard/DashboardMuiCard';
import { getCommonCardProps } from '../../Search/SearchResults/SearchResults';
import { AsyncActionStatus } from '../../../actions/common.actionTypes';
import { buildProfileFilterCascade, TitleSegments } from '../../../utils/profileFilters';
import searchFiltersToQuery from '../../../utils/searchFiltersToQuery';
import useProfileProductRow, {
  PROFILE_ROW_MIN_ITEMS,
} from '../../../routes/DashboardPage/hooks/useProfileProductRow';
import DashboardRowShell from '../DashboardRowShell/DashboardRowShell';

interface OwnProps {
  subject: Subject;
  // Class years already mapped from the user's school stages (may be empty).
  classYears: number[];
  schoolType?: string | null;
}

/**
 * A profile-aware per-subject product row (PRO-4). Title, displayed results, and
 * "Alle anzeigen" all derive from the same effective fallback level (see
 * `useProfileProductRow` + `buildProfileFilterCascade`). Hidden when fewer than
 * PROFILE_ROW_MIN_ITEMS remain after the final fallback level.
 */
const ProfileSubjectRow: React.FC<OwnProps> = ({ subject, classYears, schoolType }) => {
  const { t } = useTranslation();
  const { router } = useRouter();

  const cascade = useMemo(
    () => buildProfileFilterCascade({ subject: subject.id, classYears, schoolType }),
    [subject.id, classYears, schoolType]
  );

  const { products, status, effectiveFilters, titleSegments, isFinalLevel } =
    useProfileProductRow(cascade);

  const title = useMemo(() => buildRowTitle(titleSegments, subject.text, t), [
    titleSegments,
    subject.text,
    t,
  ]);

  const isShort = products.length < PROFILE_ROW_MIN_ITEMS;
  // A level that resolved short but can still relax is a transient state — keep
  // skeletons up until the cascade settles, so we never paint the short row (3–4
  // cards under the stricter title) for one frame before the advance re-fetches.
  const isRelaxing = status === AsyncActionStatus.READY && isShort && !isFinalLevel;
  const isLoading =
    status === AsyncActionStatus.INITIAL ||
    status === AsyncActionStatus.LOADING ||
    isRelaxing;

  // A failed fetch hides the row rather than breaking the rest of the dashboard;
  // so does a row still short after the final fallback level.
  const hidden =
    status === AsyncActionStatus.ERROR || (!isLoading && isFinalLevel && isShort);

  const onShowAllClick = () => {
    const searchParams: SearchFilters = { ...effectiveFilters, sortBy: 'createdDate' };
    const qs = searchFiltersToQuery(searchParams);
    router.push(`/${routes.search.root}/${SearchResultsTab.PRODUCTS}?${qs}`);
  };

  return (
    <DashboardRowShell title={title} onShowAll={onShowAllClick} loading={isLoading} hidden={hidden}>
      {products.map((product: Product) => (
        <DashboardMuiCard
          key={product.id}
          {...getCommonCardProps({ ...product, type: 'product' })}
          contentType='product'
          dashboardSection={dashboardSection.userSubject}
        />
      ))}
    </DashboardRowShell>
  );
};

// `Neu für dich: {subject} · {schoolType} · Kl. {range}`, mirroring the effective
// level — relaxed/absent segments are simply not present in `titleSegments`.
const buildRowTitle = (
  segments: TitleSegments,
  subjectText: string,
  t: (key: string) => string
): string => {
  const parts = [subjectText];
  if (segments.schoolType) parts.push(segments.schoolType);
  if (segments.classYearRange) parts.push(`${t('Grade abbreviation')} ${segments.classYearRange}`);
  return `${t('New for you')}: ${parts.join(' · ')}`;
};

export default ProfileSubjectRow;
